/* =========================================================
   4PIXELS — CART RUNTIME V12
   Single runtime owner for cart re-hydration, close glyph and preserving the
   header's pre-cart visual state. No injected duplicate CSS.
   ========================================================= */

(() => {
  'use strict';

  if (window.__fpCartRuntimeV12) return;

  const q = (selector, root = document) => root.querySelector(selector);
  let raf = 0;
  let detailsObserver = null;
  let observedDetails = null;

  function updateCartHeaderBubble(sectionHtml, fallbackCount) {
    const target = document.getElementById('cart-icon-bubble');
    if (!target) return;

    let incoming = null;
    if (sectionHtml) {
      try {
        const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        incoming = doc.querySelector('.cart-count-bubble');
      } catch (_error) {}
    }

    let bubble = target.querySelector('[data-cart-count], .cart-count-bubble');
    if (!bubble) {
      bubble = document.createElement('span');
      bubble.className = 'fp-header__count cart-count-bubble';
      bubble.setAttribute('data-cart-count', '');
      target.appendChild(bubble);
    }

    if (incoming) {
      bubble.classList.toggle('cart-count-bubble--dot', incoming.classList.contains('cart-count-bubble--dot'));
      bubble.innerHTML = incoming.innerHTML;
      const aria = incoming.getAttribute('aria-label');
      if (aria) bubble.setAttribute('aria-label', aria);
      bubble.hidden = false;
      bubble.removeAttribute('hidden');
      return;
    }

    const count = Math.max(0, Number(fallbackCount || 0));
    bubble.classList.toggle('cart-count-bubble--dot', count >= 100);
    bubble.innerHTML = count < 100 ? `<span aria-hidden="true">${count}</span>` : '';
    bubble.setAttribute('aria-label', `${count} cart items`);
    bubble.hidden = false;
    bubble.removeAttribute('hidden');
  }

  window.__fpUpdateCartHeaderBubble = window.__fpUpdateCartHeaderBubble || updateCartHeaderBubble;

  function important(element, property, value) {
    if (element) element.style.setProperty(property, value, 'important');
  }

  function clear(element, properties) {
    if (!element) return;
    properties.forEach((property) => element.style.removeProperty(property));
  }

  function ensureCloseGlyph() {
    const miniCart = document.getElementById('mini-cart');
    if (!miniCart) return;

    const button = q('.mini-cart__header > button.header__icon--cart', miniCart);
    if (!button) return;

    button.setAttribute('aria-label', document.documentElement.dir === 'rtl' ? 'إغلاق السلة' : 'Close cart');

    button.querySelectorAll(':scope > svg:not(.fp-cart-close-glyph)').forEach((svg) => svg.remove());

    let glyph = q(':scope > svg.fp-cart-close-glyph', button);
    if (!glyph) {
      glyph = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      glyph.classList.add('fp-cart-close-glyph');
      glyph.setAttribute('viewBox', '0 0 24 24');
      glyph.setAttribute('aria-hidden', 'true');
      glyph.setAttribute('focusable', 'false');

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M6.5 6.5 17.5 17.5M17.5 6.5 6.5 17.5');
      glyph.appendChild(path);
      button.appendChild(glyph);
    }
  }

  const shellProps = [
    '--fp-current-bg','--fp-current-text','--fp-current-icon',
    'background','background-color','background-image','color',
    'border','border-bottom','box-shadow'
  ];

  function clearHeaderLock() {
    const shell = q('[data-fp-header-shell]');
    if (!shell) return;

    clear(shell, shellProps);
    clear(q(':scope > .fp-header', shell), [
      'background','background-color','background-image','color','border','border-bottom','box-shadow'
    ]);

    shell.querySelectorAll('.fp-header__menu-toggle,.fp-header__actions .fp-header__icon,.fp-header__brand-link,.fp-header__brand-text,.fp-header__desktop-nav .header__menu-item')
      .forEach((element) => clear(element, ['color','-webkit-text-fill-color']));

    shell.querySelectorAll('.fp-header__logo').forEach((logo) => clear(logo, ['display']));
    shell.querySelectorAll('.fp-header__logo img').forEach((image) => clear(image, ['background','filter']));
  }

  function lockHeaderWhileCartOpen() {
    const shell = q('[data-fp-header-shell]');
    const details = shell && q('[data-fp-cart-details]', shell);

    if (!shell || !details || !details.open) {
      clearHeaderLock();
      return;
    }

    clearHeaderLock();

    const header = q(':scope > .fp-header', shell);
    const computed = getComputedStyle(shell);
    const transparent = shell.classList.contains('is-transparent-capable') && !shell.classList.contains('is-scrolled');

    if (transparent) {
      const text = computed.getPropertyValue('--fp-transparent-text').trim() || '#FFFFFF';

      important(shell, '--fp-current-bg', 'transparent');
      important(shell, '--fp-current-text', text);
      important(shell, '--fp-current-icon', '#FFFFFF');
      important(shell, 'background', 'transparent');
      important(shell, 'background-color', 'transparent');
      important(shell, 'background-image', 'none');
      important(shell, 'color', text);
      important(shell, 'border', '0');
      important(shell, 'border-bottom', '0');
      important(shell, 'box-shadow', 'none');

      important(header, 'background', 'transparent');
      important(header, 'background-color', 'transparent');
      important(header, 'background-image', 'none');
      important(header, 'color', text);
      important(header, 'border', '0');
      important(header, 'border-bottom', '0');
      important(header, 'box-shadow', 'none');

      shell.querySelectorAll('.fp-header__menu-toggle,.fp-header__actions .fp-header__icon,.fp-header__brand-link,.fp-header__brand-text,.fp-header__desktop-nav .header__menu-item')
        .forEach((element) => {
          important(element, 'color', '#FFFFFF');
          important(element, '-webkit-text-fill-color', '#FFFFFF');
        });

      shell.querySelectorAll('.fp-header__logo').forEach((logo) => important(logo, 'display', 'none'));
      const normalLogo = q('.fp-header__logo--normal', shell);
      if (normalLogo) {
        important(normalLogo, 'display', 'block');
        const image = q('img', normalLogo);
        if (image) {
          important(image, 'background', 'transparent');
          important(image, 'filter', 'brightness(0) invert(1)');
        }
      }
      return;
    }

    const bg = computed.getPropertyValue('--fp-solid-bg').trim() || '#FFFFFF';
    const text = computed.getPropertyValue('--fp-solid-text').trim() || '#000000';
    const icon = computed.getPropertyValue('--fp-solid-icon').trim() || '#000000';

    important(shell, '--fp-current-bg', bg);
    important(shell, '--fp-current-text', text);
    important(shell, '--fp-current-icon', icon);
    important(shell, 'background', bg);
    important(shell, 'background-color', bg);
    important(shell, 'background-image', 'none');
    important(shell, 'color', text);

    important(header, 'background', 'transparent');
    important(header, 'background-color', 'transparent');
    important(header, 'background-image', 'none');
    important(header, 'color', text);

    shell.querySelectorAll('.fp-header__menu-toggle,.fp-header__actions .fp-header__icon')
      .forEach((element) => {
        important(element, 'color', icon);
        important(element, '-webkit-text-fill-color', icon);
      });
  }

  function observeCurrentNodes() {
    const shell = q('[data-fp-header-shell]');
    const details = shell && q('[data-fp-cart-details]', shell);
    if (details && details !== observedDetails && 'MutationObserver' in window) {
      if (detailsObserver) detailsObserver.disconnect();
      observedDetails = details;
      detailsObserver = new MutationObserver(schedule);
      detailsObserver.observe(details, { attributes: true, attributeFilter: ['open','class'] });
    }

  }

  function run() {
    ensureCloseGlyph();
    lockHeaderWhileCartOpen();
    observeCurrentNodes();
  }

  function schedule() {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      run();
    });
  }

  document.addEventListener('cartdrawer:hydrated', schedule);
  document.addEventListener('cart:updated', schedule);
  document.addEventListener('cart:refresh', schedule);
  document.addEventListener('ajaxProduct:added', schedule);
  document.addEventListener('shopify:section:load', schedule);
  window.addEventListener('pageshow', schedule);
  window.addEventListener('resize', schedule, { passive: true });

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-fp-cart-details] summary,.mini-cart__header > button.header__icon--cart')) {
      setTimeout(schedule, 0);
      setTimeout(schedule, 180);
    }
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule, { once: true });
  else schedule();

  window.__fpCartRuntimeV12 = { run, schedule, ensureCloseGlyph, lockHeaderWhileCartOpen };
})();

class MiniCart extends HTMLElement {
  constructor() {
    super();
    this.drawer = null;
    this.renderVersion = 0;
    this._hydrated = false;
    this._hydrationPromise = null;
    this._hydrationToken = 0;
    this._hydrationController = null;
    this._timeoutId = null;
  }

  connectedCallback() {
    this.drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
    const details = this.drawer && this.drawer.querySelector('.cart-drawer-container');
    if (details) {
      this._onOpen = () => { if (details.open && !this._hydrated) this.hydrate(); };
      details.addEventListener('toggle', this._onOpen);
      const summary = details.querySelector(':scope > summary');
      if (summary) {
        summary.addEventListener('pointerdown', () => this.hydrate(), { passive: true });
        summary.addEventListener('focus', () => this.hydrate(), { passive: true });
      }
    }
    /* Deliberately no idle prefetch. The former prefetch raced with Add to Cart,
       discarded newer cart updates, and sometimes left the loading spinner. */
  }

  disconnectedCallback() {
    if (this._hydrationController) this._hydrationController.abort();
    if (this._timeoutId) window.clearTimeout(this._timeoutId);
    const details = this.drawer && this.drawer.querySelector('.cart-drawer-container');
    if (details && this._onOpen) details.removeEventListener('toggle', this._onOpen);
  }

  _sectionUrls() {
    const urls = [];
    const previewId = new URL(window.location.href).searchParams.get('preview_theme_id') ||
      (window.Shopify && window.Shopify.designMode && window.Shopify.theme && window.Shopify.theme.id
        ? String(window.Shopify.theme.id) : '');
    const push = (raw) => {
      const url = new URL(raw, window.location.origin);
      url.searchParams.set('section_id', 'mini-cart');
      if (previewId) url.searchParams.set('preview_theme_id', previewId);
      if (!urls.includes(url.toString())) urls.push(url.toString());
    };
    push(this.dataset.url || '/?section_id=mini-cart');
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    push(root.replace(/\/?$/, '/') + 'cart?section_id=mini-cart');
    return urls;
  }

  hydrate(force = false) {
    if (this._hydrated && !force) return Promise.resolve();
    if (this._hydrationPromise && !force) return this._hydrationPromise;

    /* Invalidate a stale request before starting a new one. */
    if (force && this._hydrationController) this._hydrationController.abort();
    if (this._timeoutId) window.clearTimeout(this._timeoutId);
    const token = ++this._hydrationToken;
    this._hydrated = false;
    this.setAttribute('aria-busy', 'true');
    const controller = new AbortController();
    this._hydrationController = controller;
    this._timeoutId = window.setTimeout(() => controller.abort(), 7000);

    const task = (async () => {
      try {
        let content = '';
        for (const url of this._sectionUrls()) {
          try {
            const response = await fetch(url, {
              credentials: 'same-origin',
              signal: controller.signal,
              cache: 'no-store',
              headers: { 'X-Requested-With': 'XMLHttpRequest' }
            });
            if (!response.ok) throw new Error('Cart section HTTP ' + response.status);
            content = this.getSectionInnerHTML(await response.text(), '.shopify-section');
            if (content && /<form\b[^>]*class=["'][^"']*mini-cart/.test(content)) break;
            content = '';
          } catch (error) {
            if (controller.signal.aborted) throw error;
            console.warn('Trying alternate cart section URL:', error);
          }
        }
        if (!content) throw new Error('Shopify did not return usable mini-cart HTML');
        if (token !== this._hydrationToken || !this.isConnected) return;
        this.innerHTML = content;
        this._hydrated = true;
        this.removeAttribute('aria-busy');
        document.dispatchEvent(new CustomEvent('cartdrawer:hydrated'));
        if (window.__fpCartRuntimeV12) window.__fpCartRuntimeV12.schedule();
      } catch (error) {
        if (token !== this._hydrationToken || !this.isConnected) return;
        console.error('Unable to render the cart section:', error);
        await this.renderLoadFallback();
      } finally {
        if (token === this._hydrationToken) {
          if (this._timeoutId) window.clearTimeout(this._timeoutId);
          this._timeoutId = null;
          this._hydrationController = null;
          this._hydrationPromise = null;
        }
      }
    })();
    this._hydrationPromise = task;
    return task;
  }

  async renderLoadFallback() {
    /* If the Sections API is unavailable in the theme editor, Shopify's
       standard Cart AJAX API still provides the actual cart contents. */
    const token = this._hydrationToken;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const cartUrl = (window.routes && window.routes.cart_url) || (root.replace(/\/?$/, '/') + 'cart');
    const ar = String(document.documentElement.lang || '').toLowerCase().startsWith('ar');
    const snapshotController = new AbortController();
    const snapshotTimeout = window.setTimeout(() => snapshotController.abort(), 4500);
    try {
      const response = await fetch(root.replace(/\/?$/, '/') + 'cart.js', {
        credentials: 'same-origin', cache: 'no-store', signal: snapshotController.signal
      });
      if (!response.ok) throw new Error('Cart JSON HTTP ' + response.status);
      const cart = await response.json();
      if (!this.isConnected || this._hydrationToken !== token) return;

      const make = (tag, className, textContent) => {
        const node = document.createElement(tag);
        if (className) node.className = className;
        if (textContent !== undefined) node.textContent = textContent;
        return node;
      };
      const money = (cents) => {
        try {
          return new Intl.NumberFormat(ar ? 'ar-EG' : 'en-EG', {
            style: 'currency', currency: cart.currency || 'EGP'
          }).format(Number(cents || 0) / 100);
        } catch (_error) {
          return ((Number(cents || 0) / 100).toFixed(2)) + ' ' + (cart.currency || 'EGP');
        }
      };
      const form = make('form', 'mini-cart clozs-cart-snapshot');
      form.action = cartUrl;
      form.method = 'post';
      form.style.cssText = 'display:flex;flex-direction:column;width:100%;height:100%;background:#fff;color:#111;';
      const inner = make('div', 'mini-cart__inner');
      inner.style.cssText = 'display:flex;flex-direction:column;height:100%;min-height:0;';
      const header = make('div', 'mini-cart__header');
      header.style.cssText = 'display:flex;align-items:center;justify-content:space-between;padding:18px 20px;border-bottom:1px solid #eee;flex:0 0 auto;';
      header.appendChild(make('h2', 'title h4', ar ? 'سلة التسوق' : 'Cart'));
      const close = make('button', '', '×');
      close.type = 'button';
      close.setAttribute('aria-label', ar ? 'إغلاق السلة' : 'Close cart');
      close.style.cssText = 'border:0;border-radius:50%;background:#111;color:white;width:36px;height:36px;font-size:24px;line-height:1;cursor:pointer;';
      close.addEventListener('click', () => {
        const details = this.closest('.cart-drawer-container');
        const summary = details && details.querySelector(':scope > summary');
        if (summary) summary.click();
      });
      header.appendChild(close);
      const main = make('div', 'mini-cart__main');
      main.style.cssText = 'flex:1 1 auto;overflow:auto;padding:18px 20px;min-height:0;';
      const items = Array.isArray(cart.items) ? cart.items : [];
      if (!items.length) {
        main.appendChild(make('p', '', ar ? 'السلة فارغة' : 'Your bag is empty'));
      }
      const changeQuantity = async (line, quantity, button) => {
        if (button) button.disabled = true;
        try {
          const response = await fetch(root.replace(/\/?$/, '/') + 'cart/change.js', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ line, quantity })
          });
          if (!response.ok) throw new Error('Could not change cart quantity');
          if (this.isConnected) {
            this._hydrated = false;
            await this.renderLoadFallback();
          }
        } catch (error) {
          console.error('Cart quantity update:', error);
          if (button) button.disabled = false;
          const warning = make('p', '', ar ? 'تعذّر تحديث الكمية. حاول مرة أخرى.' : 'Could not update quantity. Please retry.');
          warning.style.cssText = 'font-size:12px;color:#b00020;';
          main.prepend(warning);
        }
      };
      items.forEach((item, index) => {
        const row = make('div', 'clozs-cart-snapshot__item');
        row.style.cssText = 'display:flex;gap:12px;padding:14px 0;border-bottom:1px solid #eee;align-items:flex-start;';
        if (item.image) {
          const picture = make('img');
          picture.src = item.image;
          picture.alt = item.product_title || '';
          picture.loading = 'lazy';
          picture.style.cssText = 'width:74px;height:94px;object-fit:cover;flex:none;border-radius:7px;';
          row.appendChild(picture);
        }
        const info = make('div');
        info.style.cssText = 'display:flex;flex-direction:column;gap:7px;min-width:0;flex:1;';
        const title = make('a', '', item.product_title || item.title || '');
        title.href = item.url || '#';
        title.style.cssText = 'font-size:13px;font-weight:600;color:#111;text-decoration:none;';
        info.appendChild(title);
        if (item.variant_title && item.variant_title !== 'Default Title') {
          const variant = make('span', '', item.variant_title);
          variant.style.cssText = 'font-size:12px;color:#666;';
          info.appendChild(variant);
        }
        info.appendChild(make('strong', '', money(item.final_line_price)));
        const controls = make('div');
        controls.style.cssText = 'display:flex;align-items:center;gap:8px;';
        const minus = make('button', '', '−');
        minus.type = 'button';
        minus.setAttribute('aria-label', ar ? 'تقليل الكمية' : 'Decrease quantity');
        minus.addEventListener('click', () => changeQuantity(index + 1, Math.max(0, item.quantity - 1), minus));
        const count = make('span', '', String(item.quantity));
        count.style.cssText = 'min-width:20px;text-align:center;';
        const plus = make('button', '', '+');
        plus.type = 'button';
        plus.setAttribute('aria-label', ar ? 'زيادة الكمية' : 'Increase quantity');
        plus.addEventListener('click', () => changeQuantity(index + 1, item.quantity + 1, plus));
        for (const b of [minus, plus]) b.style.cssText = 'width:30px;height:30px;border:1px solid #ddd;background:#fff;color:#111;border-radius:6px;cursor:pointer;font-size:17px;';
        controls.append(minus, count, plus);
        info.appendChild(controls);
        row.appendChild(info);
        main.appendChild(row);
      });
      const footer = make('div', 'mini-cart__footer');
      footer.style.cssText = 'display:flex;flex-direction:column;gap:11px;padding:18px 20px;border-top:1px solid #eee;background:white;flex:none;';
      const subtotal = make('div');
      subtotal.style.cssText = 'display:flex;align-items:center;justify-content:space-between;';
      subtotal.append(make('span', '', ar ? 'الإجمالي' : 'Subtotal'), make('strong', '', money(cart.total_price)));
      footer.appendChild(subtotal);
      const checkout = make('button', 'button', ar ? 'إتمام الشراء' : 'CHECKOUT');
      checkout.type = 'submit';
      checkout.name = 'checkout';
      checkout.style.cssText = 'width:100%;min-height:45px;background:#000;color:#fff;border-radius:9px;cursor:pointer;border:0;';
      footer.appendChild(checkout);
      const view = make('a', 'button button--secondary', ar ? 'عرض السلة' : 'VIEW CART');
      view.href = cartUrl;
      view.style.cssText = 'display:flex;justify-content:center;align-items:center;min-height:42px;color:#111;border:1px solid #ddd;border-radius:9px;text-decoration:none;';
      footer.appendChild(view);
      inner.append(header, main, footer);
      form.appendChild(inner);
      this.replaceChildren(form);
      this._hydrated = true;
      this.removeAttribute('aria-busy');
      if (window.__fpUpdateCartHeaderBubble) window.__fpUpdateCartHeaderBubble(null, cart.item_count);
      if (window.__fpCartRuntimeV12) window.__fpCartRuntimeV12.schedule();
    } catch (error) {
      if (!this.isConnected || token !== this._hydrationToken) return;
      console.error('Cart JSON recovery failed:', error);
      this._hydrated = false;
      this.removeAttribute('aria-busy');
      const fallback = document.createElement('div');
      fallback.style.cssText = 'display:flex;flex-direction:column;gap:14px;padding:35px 20px;text-align:center;color:#111;background:#fff;';
      const msg = document.createElement('p');
      msg.textContent = ar ? 'تعذّر تحميل السلة. يمكنك إعادة المحاولة أو عرض صفحة السلة.' : 'The cart did not load. Please retry or view your cart.';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'button';
      retry.textContent = ar ? 'إعادة المحاولة' : 'Retry';
      retry.addEventListener('click', () => { this.hydrate(true); });
      const view = document.createElement('a');
      view.href = cartUrl;
      view.textContent = ar ? 'عرض السلة' : 'View cart';
      fallback.append(msg, retry, view);
      this.replaceChildren(fallback);
    } finally {
      window.clearTimeout(snapshotTimeout);
    }
  }

  open() {
    this.drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
    const details = this.drawer && this.drawer.querySelector('.cart-drawer-container');
    if (!details) return;
    if (!this._hydrated) this.hydrate();

    if (details.hasAttribute('open')) {
      if (this.drawer && typeof this.drawer.cancelCloseAnimation === 'function') {
        this.drawer.cancelCloseAnimation();
      } else {
        details.classList.remove('menu-closing');
      }
      return;
    }
    const summary = details.querySelector(':scope > summary');
    if (summary) summary.click();
  }

  renderContents(parsedState) {
    /* A successful Add to Cart invalidates any older section fetch. */
    this.renderVersion = (this.renderVersion || 0) + 1;
    this.productId = parsedState && parsedState.id;
    ++this._hydrationToken;
    if (this._hydrationController) this._hydrationController.abort();
    if (this._timeoutId) window.clearTimeout(this._timeoutId);
    this._hydrationPromise = null;
    this._hydrationController = null;
    this._timeoutId = null;

    const html = parsedState && parsedState.sections && parsedState.sections['mini-cart'];
    const content = html && this.getSectionInnerHTML(html, '.shopify-section');
    if (content && /<form\b[^>]*class=["'][^"']*mini-cart/.test(content)) {
      this.innerHTML = content;
      this._hydrated = true;
      this.removeAttribute('aria-busy');
      const bubbleHtml = parsedState.sections['cart-icon-bubble'];
      if (bubbleHtml && window.__fpUpdateCartHeaderBubble) {
        window.__fpUpdateCartHeaderBubble(bubbleHtml);
      }
      document.dispatchEvent(new CustomEvent('cartdrawer:hydrated'));
      this.open();
      this.showAddedInDrawer();
      if (window.__fpCartRuntimeV12) window.__fpCartRuntimeV12.schedule();
    } else {
      /* The sections API can return null/empty in theme-preview mode. Reload
         the whole cart section instead of leaving a permanent spinner. */
      this._hydrated = false;
      this.open();
    }
  }

  showAddedInDrawer() {
    const title = this.querySelector('.mini-cart__header > .title');
    if (!title) return;
    const prior = title.querySelector('.clozs-cart-added-note');
    if (prior) prior.remove();
    const note = document.createElement('span');
    note.className = 'clozs-cart-added-note';
    note.setAttribute('role', 'status');
    const ar = String(document.documentElement.lang || '').toLowerCase().startsWith('ar');
    note.textContent = ar ? '✓ تمت الإضافة' : '✓ Added to bag';
    title.appendChild(note);
    window.setTimeout(() => { if (note.isConnected) note.remove(); }, 2400);
  }

  getSectionsToRender() {
    return [
      { id: 'mini-cart', section: 'mini-cart', selector: '.shopify-section' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (typeof html !== 'string' || !html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = doc.querySelector(selector);
    if (section) return section.innerHTML;
    /* Some Shopify responses are already unwrapped section HTML. */
    return doc.querySelector('form.mini-cart') ? doc.body.innerHTML : '';
  }
}

if (!customElements.get('mini-cart')) customElements.define('mini-cart', MiniCart);
